// Who a visa product is for: which travellers are asked for each of its
// documents and questions, and which ages can apply. Pure wording and checks,
// shared by the product's document and question screens, the price form and
// the case page.
//
// The API's rule (safrati-apis docs/conditional-forms.md, migration 014): a
// document or question can apply only when one of the product's own choice
// questions has certain answers ("Employment letter" when "Employment status"
// is Employed). No condition: everyone. An item whose question is no longer
// on the product is never asked, because that question is never answered.
// A condition can also be an age, counted on the arrival date from the date
// of birth: { when_question_id: the date of birth, values: [], age: { under?,
// at_least? } } ("Child's birth certificate" under 18).

const text = (value) => String(value ?? "").trim();
const blank = (value) => text(value) === "";

/** "Employed", "Employed or Retired", "Employed, Student or Retired". */
export function joinOr(values) {
  const list = (values || []).map(text).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} or ${list[list.length - 1]}`;
}

// A label that is itself a question ("Have you been to Europe before?")
// reads as one only in quotes; a plain one ("Employment status") doesn't need
// them.
const subject = (label) => {
  const name = text(label);
  return /[?:]$/.test(name) ? `“${name}”` : name;
};

const labelOf = (question) => (typeof question === "string" ? question : question?.label ?? question?.question ?? "");

/**
 * Who an item is for, in one line: "Everyone", or "Only when Employment
 * status is Employed or Retired". `question` is the label, or an object with
 * `label` (price/conditions) or `question` (a case's show_when).
 */
export function conditionText(question, values) {
  const answers = joinOr(values);
  if (!answers) return "Everyone";
  const name = labelOf(question);
  return blank(name) ? `Only when an earlier answer is ${answers}` : `Only when ${subject(name)} is ${answers}`;
}

/**
 * Why a case asks this traveller for a document, from its `show_when`
 * ({ question_id, question, values }): "Asked because Employment status is
 * Employed". Null when it is asked of everyone. `answer`, the traveller's
 * own answer to that question, names just that one when it is one of them.
 */
export function askedBecause(showWhen, answer) {
  if (showWhen?.age) return `Asked because they’re ${ageWords(showWhen.age).toLowerCase()} on arrival`;
  const own = (showWhen?.values || []).filter((value) => !blank(answer) && text(value).toLowerCase() === text(answer).toLowerCase());
  const answers = joinOr(own.length ? own.slice(0, 1) : showWhen?.values);
  if (!answers) return null;
  const name = labelOf(showWhen);
  return blank(name) ? `Asked because an earlier answer is ${answers}` : `Asked because ${subject(name)} is ${answers}`;
}

/** The saved condition on one item, or null when it applies to everyone. */
export function conditionFor(conditions, itemType, itemId) {
  const found = (conditions || []).find(
    (row) => row.item_type === itemType && Number(row.item_id) === Number(itemId)
  );
  return found && found.when_question_id && ((found.values || []).some((value) => !blank(value)) || found.age) ? found : null;
}

/** "Under 18", "65 or older", "16 to 17". */
export function ageWords(age) {
  if (!age) return "";
  if (age.under != null && age.at_least != null) return `${age.at_least} to ${age.under - 1}`;
  if (age.under != null) return `Under ${age.under}`;
  if (age.at_least != null) return `${age.at_least} or older`;
  return "";
}

/** "Only travellers under 18 on arrival". */
export const ageConditionText = (age) => `Only travellers ${ageWords(age).toLowerCase()} on arrival`;

/** "only of travellers under 18 on arrival", after "is asked". */
export const ageAskedOf = (age) => `only of travellers ${ageWords(age).toLowerCase()} on arrival`;

/** Whether the product asks for a date of birth (so an item can go by age), as the API finds it. */
export const hasBirthDate = (questions) =>
  (questions || []).some(
    (q) => q.type === "date" && (q.field_key === "date_of_birth" || (!q.field_key && !q.suggested_key && /birth/i.test(q.label || "")) || q.suggested_key === "date_of_birth")
  );

/**
 * An age rule from what staff typed ("Younger than" and "From age"):
 * { payload: { age_under?, age_at_least? } } or { errors }.
 */
export function readAgeRule({ under, atLeast }) {
  const whole = (value) => (blank(value) ? null : /^\d{1,3}$/.test(text(value)) && Number(value) <= 120 ? Number(value) : NaN);
  const u = whole(under);
  const a = whole(atLeast);
  const errors = {};
  if (Number.isNaN(u) || u === 0) errors.under = "Give an age in whole years, 1 to 120.";
  if (Number.isNaN(a)) errors.atLeast = "Give an age in whole years, 0 to 120.";
  if (!errors.under && !errors.atLeast && u == null && a == null) errors.under = "Give at least one age.";
  if (!errors.under && !errors.atLeast && u != null && a != null && u <= a) errors.under = "“Younger than” has to be more than “From age”.";
  if (Object.keys(errors).length) return { errors };
  return { payload: { ...(u != null ? { age_under: u } : {}), ...(a != null ? { age_at_least: a } : {}) }, age: { ...(u != null ? { under: u } : {}), ...(a != null ? { at_least: a } : {}) } };
}

/**
 * What the row under an item says: { tone, text }. tone is "everyone",
 * "some", or "broken" when the question it depends on is no longer on the
 * product (the item is then never asked).
 */
export function whoIsAsked(condition, questions) {
  if (!condition) return { tone: "everyone", text: "Everyone" };
  const when = (questions || []).find((q) => Number(q.question_id) === Number(condition.when_question_id));
  if (!when) return { tone: "broken", text: "Never asked: the question it depends on is no longer on this product." };
  if (condition.age) return { tone: "some", text: ageConditionText(condition.age) };
  return { tone: "some", text: conditionText(when.label, condition.values) };
}

/**
 * The questions an item can depend on: the product's choice questions
 * (`choices` not null), never the item itself, and never a question that
 * already depends on it, directly or through others (the two would wait on
 * each other and neither be asked).
 */
export function dependOptions(questions, conditions, itemType, itemId) {
  const isQuestion = itemType === "question";
  const leadsBackToItem = (questionId) => {
    if (!isQuestion) return false;
    const seen = new Set();
    let current = Number(questionId);
    while (!seen.has(current)) {
      seen.add(current);
      const condition = conditionFor(conditions, "question", current);
      if (!condition) return false;
      current = Number(condition.when_question_id);
      if (current === Number(itemId)) return true;
    }
    return false;
  };
  return (questions || []).filter(
    (q) =>
      Array.isArray(q.choices) &&
      q.choices.length > 0 &&
      !(isQuestion && Number(q.question_id) === Number(itemId)) &&
      !leadsBackToItem(q.question_id)
  );
}

/**
 * Saved answers in the spelling of the question's current choices (the API
 * compares them ignoring case). An answer that is no longer a choice is left
 * out, so saving again cannot be refused for it.
 */
export function matchChoices(values, choices) {
  const byLower = new Map((choices || []).map((choice) => [text(choice).toLowerCase(), choice]));
  const out = [];
  (values || []).forEach((value) => {
    const choice = byLower.get(text(value).toLowerCase());
    if (choice !== undefined && !out.includes(choice)) out.push(choice);
  });
  return out;
}

/** The conditions that wait on one question's answer. */
export const dependentsOf = (conditions, questionId) =>
  (conditions || []).filter(
    (row) => Number(row.when_question_id) === Number(questionId) && ((row.values || []).some((value) => !blank(value)) || row.age)
  );

const joinAnd = (names) => (names.length <= 1 ? names[0] || "" : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

/**
 * Taking questions off a product: one warning per removed question that
 * other items still depend on, since those would never be asked. `form` is
 * price/conditions' { questions, documents }. Items being taken off too are
 * not counted.
 */
export function strandedWarnings(conditions, removedIds, form) {
  const removed = new Set((removedIds || []).map(Number));
  const nameOf = (row) => {
    if (row.item_type === "document") return form?.documents?.find((doc) => Number(doc.document_id) === Number(row.item_id))?.name;
    const label = form?.questions?.find((q) => Number(q.question_id) === Number(row.item_id))?.label;
    return label ? subject(label) : null;
  };
  const count = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  return [...removed].flatMap((questionId) => {
    const items = dependentsOf(conditions, questionId)
      .filter((row) => !(row.item_type === "question" && removed.has(Number(row.item_id))))
      .map((row) => ({ type: row.item_type, name: nameOf(row) }))
      .filter((item) => item.name);
    if (!items.length) return [];
    const label = form?.questions?.find((q) => Number(q.question_id) === questionId)?.label || "this question";
    const one = items.length === 1;
    // Two are named; more are counted (each says what it waits on in the list).
    const documents = items.filter((item) => item.type === "document").length;
    const what =
      items.length <= 2
        ? joinAnd(items.map((item) => item.name))
        : [documents ? count(documents, "document") : "", items.length - documents ? count(items.length - documents, "question") : ""]
            .filter(Boolean)
            .join(" and ");
    return [
      `${what} ${one ? "depends" : "depend"} on ${subject(label)}. If you take it off, ${one ? "it is" : "they are"} never asked. Set ${one ? "it" : "them"} to Everyone first, or keep the question.`,
    ];
  });
}

// ---- Who can apply, by age --------------------------------------------------

// Ages on visa_type_price (migration 014): whole numbers from 0 to 120, either
// may be empty (no limit that side). Counted on the arrival date.

export const AGE_FIELDS = ["min_age", "max_age"];
const AGE_LIMIT = 120;

/** True once the API returns the age fields on product rows. */
export const hasAgeFields = (rows) => (rows || []).some((row) => row && "min_age" in row);

/** The form's values for the ages, from a product row. */
export const ageValues = (row) => ({
  min_age: blank(row?.min_age) ? "" : String(Number(row.min_age)),
  max_age: blank(row?.max_age) ? "" : String(Number(row.max_age)),
});

// A whole number from 0 to 120, null when empty, NaN when it isn't one.
const readAge = (value) => {
  if (blank(value)) return null;
  const n = Number(text(value));
  return Number.isInteger(n) && n >= 0 && n <= AGE_LIMIT ? n : NaN;
};

/** Errors keyed by field name, the same rules as the API's price/edit. */
export function validateAges(values) {
  const errors = {};
  const min = readAge(values?.min_age);
  const max = readAge(values?.max_age);
  const message = "Enter a whole number from 0 to 120.";
  if (Number.isNaN(min)) errors.min_age = message;
  if (Number.isNaN(max)) errors.max_age = message;
  if (min !== null && max !== null && !Number.isNaN(min) && !Number.isNaN(max) && min > max) {
    errors.max_age = "The oldest age can't be less than the youngest.";
  }
  return errors;
}

/** The two API fields, from the form values: a number, or null for no limit. */
export function agePayload(values) {
  const min = readAge(values?.min_age);
  const max = readAge(values?.max_age);
  return { min_age: Number.isNaN(min) ? null : min, max_age: Number.isNaN(max) ? null : max };
}

/**
 * The age fields to send. A new product sends what was entered; an edit only
 * what changed from the row as loaded, so a save never clears limits staff
 * didn't touch (and an untouched edit sends nothing the API might not know).
 */
export function ageChanges(values, originalRow, { isNew } = {}) {
  const next = agePayload(values);
  if (isNew) return Object.fromEntries(Object.entries(next).filter(([, value]) => value !== null));
  const before = agePayload(ageValues(originalRow || {}));
  return Object.fromEntries(Object.entries(next).filter(([key, value]) => value !== before[key]));
}

/**
 * Who can apply, in the website's words ("aged 16 to 50 on arrival"): a
 * sentence, or "" when the values aren't valid ages yet.
 */
export function ageLimitText(values) {
  if (Object.keys(validateAges(values)).length) return "";
  const { min_age: min, max_age: max } = agePayload(values);
  const from = min === 0 ? null : min;
  if (from === null && max === null) return "Travellers of any age can apply.";
  let range;
  if (from !== null && max !== null) range = from === max ? `aged ${from}` : `aged ${from} to ${max}`;
  else if (from !== null) range = `aged ${from} or over`;
  else range = `aged ${max} or under`;
  return `Only travellers ${range} on arrival can apply.`;
}

// ---- the standard set ------------------------------------------------------
// What the API's standard set would do on a product (price/conditions/standard):
// each question and document with action on_product | link | create | skip |
// not_asked, and its condition { status: set | kept | no_answers |
// no_question, question, values, missing }.

/** The preview's groups of documents, in the order staff read them. */
export const STANDARD_GROUPS = [
  { key: "everyone", title: "Everyone" },
  { key: "work", title: "By work" },
  { key: "funds", title: "Paying for the trip themselves" },
  { key: "guarantor", title: "When a guarantor pays" },
  { key: "invitation", title: "When someone invites them" },
];

const quoted = (values) => joinOr((values || []).map((value) => `“${text(value)}”`));

/**
 * One line of the preview: { name, state, text, who, note }. state: "add"
 * (from the library), "new" (new to the library too), "have" (already on the
 * product) or "left" (left out, with why). who: who will be asked. note: an
 * answer the product's question lacks, so that part is left out.
 */
export function standardRow(entry) {
  const rule = entry?.condition || null;
  const name = text(entry?.name ?? entry?.label);
  if (entry?.action === "skip") {
    const why =
      rule?.status === "no_answers"
        ? `${subject(rule.question)} has no answer ${quoted(rule.missing)}. Add it to that question, then add the set again.`
        : `this product doesn't ask ${subject(rule?.question) || "the question it depends on"}.`;
    return { name, state: "left", text: `Left out: ${why}`, who: null, note: null };
  }
  const state = entry?.action === "create" ? "new" : entry?.action === "link" ? "add" : "have";
  const stateText = { new: "Added, and new to the library", add: "Added from the library", have: "Already on this product" }[state];
  let who = null;
  if (rule?.status === "set") who = rule.age ? ageConditionText(rule.age) : conditionText(rule.question, rule.values);
  else if (rule?.status === "kept") who = "Keeps the rule already set";
  else if (!rule) who = "Everyone";
  const note = rule?.status === "set" && rule.missing?.length ? `Not for ${quoted(rule.missing)}: ${subject(rule.question)} has no such answer.` : null;
  return { name, state, text: stateText, who, note };
}

/** What adding the set would add: { questions, documents, newOnes }. */
export function standardCounts(plan) {
  const adds = (entry) => entry.action === "create" || entry.action === "link";
  const questions = (plan?.questions || []).filter(adds);
  const documents = (plan?.documents || []).filter(adds);
  return { questions: questions.length, documents: documents.length, newOnes: [...questions, ...documents].filter((e) => e.action === "create").length };
}

/** "Adds 6 questions and 20 documents." / "Everything in it is already on this product." */
export function standardSummary(counts) {
  const plural = (n, one) => `${n} ${one}${n === 1 ? "" : "s"}`;
  if (!counts.questions && !counts.documents) return "Everything in it is already on this product.";
  const parts = [counts.questions ? plural(counts.questions, "question") : null, counts.documents ? plural(counts.documents, "document") : null].filter(Boolean);
  return `Adds ${parts.join(" and ")}${counts.newOnes ? `, ${counts.newOnes} of them new to the library` : ""}.`;
}

// ---- the embassy check -----------------------------------------------------
// What the API read on an embassy's checklist (price/conditions/embassy):
// each document as the standard set's preview entry, plus from_page: the
// name, quote, who ({ key, values, group_quote, status: verified | unchecked
// | unverified }) and note (note_status) as read and checked on the page.

/**
 * One line of the review: the standard row's words, what the checklist calls
 * it, its quote, what failed a check (checks), and whether it starts ticked
 * (only what passed every check and isn't left out).
 */
export function embassyRow(entry) {
  const row = standardRow(entry);
  const page = entry?.from_page || {};
  const checks = [];
  if (!page.verified) checks.push("Not found on the checklist as quoted. Read the checklist before adding it.");
  if (page.who?.status === "unverified") {
    checks.push(
      `Who it’s for isn’t clear on the checklist${page.who.group_quote ? ` (“${text(page.who.group_quote)}”)` : ""}, so it would be asked of everyone.`
    );
  }
  if (page.who?.status === "unchecked") checks.push("Who it’s for couldn’t be checked in this language. Read it on the checklist.");
  if (page.note_status === "unverified" && page.note) checks.push(`Note left out: a number in it isn’t on the checklist (“${text(page.note)}”).`);
  const pageName = text(page.name);
  return {
    ...row,
    calledOnPage: pageName && pageName.toLowerCase() !== row.name.toLowerCase() ? pageName : null,
    quote: text(page.quote) || null,
    note: entry?.note ? text(entry.note) : null,
    noteKept: entry?.note_status === "kept",
    checks,
    ticked: Boolean(page.verified) && !["unverified", "unchecked"].includes(page.who?.status) && row.state !== "left",
  };
}

/** What apply sends for the ticked entries (only what the page backed). */
export function embassyPayload(entries, ticked) {
  return (entries || [])
    .filter((entry) => ticked.has(entry.id))
    .map((entry) => {
      const page = entry.from_page || {};
      const who = page.who && page.who.status !== "unverified" ? { key: page.who.key, values: page.who.values } : null;
      return {
        name: page.name || entry.name,
        product_document_id: page.product_document_id ?? null,
        standard_id: page.standard_id ?? null,
        required: page.required !== false,
        when: who,
        note: page.note_status === "verified" && page.note ? page.note : null,
      };
    });
}

/** "12 documents on the checklist; 9 checked against it." */
export function embassySummary(entries) {
  const all = (entries || []).length;
  const checked = (entries || []).filter((entry) => embassyRow(entry).ticked).length;
  if (!all) return "No documents found on the checklist.";
  return `${all} document${all === 1 ? "" : "s"} on the checklist; ${checked} checked against it.`;
}
